import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Wallet,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Bitcoin,
  AlertCircle,
  CreditCard
} from 'lucide-react';
import { advertiserApi, AdvertiserProfile } from '../../api';
import { useTelegram } from '../../hooks';

interface Transaction {
  id: string;
  type: 'deposit' | 'ad_spend' | 'refund' | 'admin_credit';
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  note?: string;
}

interface DepositMethod {
  id: string;
  name: string;
  description: string;
  available: boolean;
  networks?: string[];
  exchangeRates?: Record<string, number>;
  currencies?: string[];
  pointsPerUsd?: number;
}

interface WalletSectionProps {
  profile: AdvertiserProfile;
  onRefresh: () => void;
}

export function WalletSection({ profile, onRefresh: _onRefresh }: WalletSectionProps) {
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositMethod, setDepositMethod] = useState<'crypto' | 'faucetpay' | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState('TRX');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [depositAddress, setDepositAddress] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [availableMethods, setAvailableMethods] = useState<DepositMethod[]>([]);
  const [faucetPayData, setFaucetPayData] = useState<any>(null);
  
  const { hapticFeedback, hapticNotification } = useTelegram();

  // Fetch transactions and deposit config on mount
  useEffect(() => {
    fetchTransactions();
    fetchDepositConfig();
  }, []);

  const fetchDepositConfig = async () => {
    try {
      const response = await advertiserApi.getDepositConfig();
      if (response.success && response.methods) {
        setAvailableMethods(response.methods);
      }
    } catch (err) {
      console.error('Failed to fetch deposit config:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await advertiserApi.getTransactions(1, 10);
      if (response.success && response.deposits) {
        setTransactions(response.deposits.map((d: any) => ({
          id: d.id,
          type: 'deposit',
          amount: d.amount,
          status: d.status,
          createdAt: d.createdAt,
          note: d.method,
        })));
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleDeposit = async () => {
    if (!depositAmount || !depositMethod) return;
    
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount < 1000) {
      setError('Minimum deposit is 1,000 points');
      return;
    }

    hapticFeedback('medium');
    setLoading(true);
    setError(null);

    try {
      const response = await advertiserApi.deposit({
        amount,
        method: depositMethod,
        network: depositMethod === 'crypto' ? selectedNetwork : undefined
      });

      if (response.success) {
        if (depositMethod === 'faucetpay' && response.faucetPay?.paymentUrl) {
          // Open FaucetPay payment page in external browser
          // Telegram Mini Apps can't POST forms to external URLs
          hapticNotification('success');
          
          // Use Telegram's openLink to open in external browser
          if (window.Telegram?.WebApp?.openLink) {
            window.Telegram.WebApp.openLink(response.faucetPay.paymentUrl);
          } else {
            // Fallback for non-Telegram environment
            window.open(response.faucetPay.paymentUrl, '_blank');
          }
          
          // Show success message and close modal
          setFaucetPayData({ opened: true, depositId: response.deposit?.id });
        } else if (response.deposit?.walletAddress) {
          setDepositAddress(response.deposit.walletAddress);
          hapticNotification('success');
        }
      } else {
        setError(response.error || 'Deposit failed');
        hapticNotification('error');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Deposit failed');
      hapticNotification('error');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    hapticNotification('success');
  };

  return (
    <div className="space-y-4">
      {/* Balance Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-4 border border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-primary" />
            <span className="text-xs text-white/60">Available</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {profile.wallet.balance.toLocaleString()}
          </p>
          <p className="text-xs text-white/50">points</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 rounded-xl p-4 border border-yellow-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-white/60">Locked</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {profile.wallet.lockedBalance.toLocaleString()}
          </p>
          <p className="text-xs text-white/50">in active ads</p>
        </div>
      </div>

      {/* Deposit Button */}
      <button
        onClick={() => {
          hapticFeedback('medium');
          setShowDeposit(true);
        }}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Add Funds
      </button>

      {/* Transaction History */}
      <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Recent Transactions
          </h3>
        </div>

        <div className="divide-y divide-white/5">
          {loadingTransactions ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-white/50">No transactions yet</p>
              <p className="text-sm text-white/30 mt-1">Add funds to get started</p>
            </div>
          ) : (
            transactions.map((tx) => (
              <div key={tx.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    tx.type === 'deposit' || tx.type === 'refund' || tx.type === 'admin_credit' ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {tx.type === 'deposit' || tx.type === 'refund' || tx.type === 'admin_credit' ? (
                      <ArrowDownRight className="w-5 h-5 text-green-400" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white capitalize">
                      {tx.type.replace('_', ' ')}
                    </p>
                    <p className="text-xs text-white/50">{new Date(tx.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${tx.type === 'ad_spend' ? 'text-red-400' : 'text-green-400'}`}>
                    {tx.type === 'ad_spend' ? '-' : '+'}{Math.abs(tx.amount).toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1 text-xs">
                    {tx.status === 'completed' ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                        <span className="text-green-400">Completed</span>
                      </>
                    ) : tx.status === 'pending' ? (
                      <>
                        <Clock className="w-3 h-3 text-yellow-400" />
                        <span className="text-yellow-400">Pending</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 text-red-400" />
                        <span className="text-red-400">Failed</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Deposit Modal */}
      {showDeposit && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => {
              setShowDeposit(false);
              setDepositAddress(null);
              setFaucetPayData(null);
              setDepositMethod(null);
              setError(null);
              setDepositAmount('');
            }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto pb-20"
          >
            <div className="bg-surface rounded-t-3xl p-6 pb-24">
              {/* FaucetPay Form (hidden, submitted via JS) */}
              {faucetPayData && (
                <form
                  id="faucetpay-form"
                  method="POST"
                  action={faucetPayData.url}
                  ref={(form) => {
                    if (form) form.submit();
                  }}
                >
                  {Object.entries(faucetPayData.params).map(([key, value]) => (
                    <input key={key} type="hidden" name={key} value={value as string} />
                  ))}
                </form>
              )}

              <h2 className="text-xl font-bold text-white mb-6">
                {!depositMethod ? 'Select Deposit Method' : depositAddress ? 'Deposit Address' : 'Add Funds'}
              </h2>

              {/* Method Selection */}
              {!depositMethod && !depositAddress && !faucetPayData && (
                <div className="space-y-4">
                  {availableMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => {
                        if (method.available) {
                          hapticFeedback('light');
                          setDepositMethod(method.id as 'crypto' | 'faucetpay');
                        }
                      }}
                      disabled={!method.available}
                      className={`w-full p-4 rounded-xl border ${
                        method.available 
                          ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                          : 'bg-white/5 border-white/5 opacity-50 cursor-not-allowed'
                      } flex items-center gap-4 transition-colors`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        method.id === 'crypto' ? 'bg-orange-500/20' : 'bg-blue-500/20'
                      }`}>
                        {method.id === 'crypto' ? (
                          <Bitcoin className="w-6 h-6 text-orange-400" />
                        ) : (
                          <CreditCard className="w-6 h-6 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-white">{method.name}</p>
                        <p className="text-sm text-white/60">{method.description}</p>
                      </div>
                      {!method.available && (
                        <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">Disabled</span>
                      )}
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      setShowDeposit(false);
                    }}
                    className="w-full py-4 rounded-xl bg-white/10 text-white font-medium mt-4"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Crypto Deposit */}
              {depositMethod === 'crypto' && !depositAddress && (
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setDepositMethod(null);
                      setError(null);
                    }}
                    className="text-sm text-primary mb-2"
                  >
                    ← Back to methods
                  </button>

                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Bitcoin className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Crypto Deposit</p>
                      <p className="text-sm text-white/60">Send crypto to receive points</p>
                    </div>
                  </div>

                  {/* Network Selection */}
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Select Network</label>
                    <div className="space-y-2">
                      {(() => {
                        const cryptoMethod = availableMethods.find(m => m.id === 'crypto');
                        const networks = cryptoMethod?.networks || [];
                        
                        // Network display info with chain details
                        const networkInfo: Record<string, { name: string; chain: string; symbol: string }> = {
                          'TRX': { name: 'TRON', chain: 'TRC20', symbol: 'TRX' },
                          'USDT_TRC20': { name: 'Tether USDT', chain: 'TRC20 Network', symbol: 'USDT' },
                          'TON': { name: 'Toncoin', chain: 'TON Network', symbol: 'TON' },
                          'BTC': { name: 'Bitcoin', chain: 'BTC Network', symbol: 'BTC' },
                          'ETH': { name: 'Ethereum', chain: 'ERC20 Network', symbol: 'ETH' },
                          'BNB': { name: 'BNB', chain: 'BEP20 (BSC)', symbol: 'BNB' },
                        };
                        
                        return networks.map((network) => {
                          const rate = cryptoMethod?.exchangeRates?.[network] || 100;
                          const info = networkInfo[network] || { name: network, chain: network, symbol: network };
                          
                          return (
                            <button
                              key={network}
                              onClick={() => setSelectedNetwork(network)}
                              className={`w-full p-4 rounded-xl border flex items-center justify-between ${
                                selectedNetwork === network
                                  ? 'border-primary bg-primary/20'
                                  : 'border-white/10 bg-white/5'
                              }`}
                            >
                              <div className="text-left">
                                <p className="font-semibold text-white">{info.name}</p>
                                <p className="text-xs text-white/50">{info.chain}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-primary">{rate.toLocaleString()} pts</p>
                                <p className="text-xs text-white/50">per 1 {info.symbol}</p>
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Conversion Calculator */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white/60">You will receive:</span>
                      <span className="text-lg font-bold text-primary">
                        {depositAmount ? parseInt(depositAmount).toLocaleString() : '0'} points
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/60">You need to send:</span>
                      <span className="text-sm font-medium text-white">
                        {(() => {
                          const cryptoMethod = availableMethods.find(m => m.id === 'crypto');
                          const rate = cryptoMethod?.exchangeRates?.[selectedNetwork] || 100;
                          const networkInfo: Record<string, string> = {
                            'TRX': 'TRX',
                            'USDT_TRC20': 'USDT',
                            'TON': 'TON',
                            'BTC': 'BTC',
                            'ETH': 'ETH',
                            'BNB': 'BNB',
                          };
                          const amount = depositAmount ? (parseFloat(depositAmount) / rate) : 0;
                          return `${amount.toFixed(6)} ${networkInfo[selectedNetwork] || selectedNetwork}`;
                        })()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-2">Amount (points)</label>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Min 1,000"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  {/* Important Notice */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                    <p className="text-yellow-400 text-xs flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Only send <strong>{selectedNetwork.replace('_', ' ')}</strong> on the correct network. 
                        Sending wrong tokens will result in permanent loss.
                      </span>
                    </p>
                  </div>

                  {error && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setDepositMethod(null);
                        setError(null);
                        setDepositAmount('');
                      }}
                      className="flex-1 py-4 rounded-xl bg-white/10 text-white font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeposit}
                      disabled={loading || !depositAmount}
                      className="flex-1 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Get Address'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* FaucetPay Deposit */}
              {depositMethod === 'faucetpay' && !faucetPayData && (
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setDepositMethod(null);
                      setError(null);
                    }}
                    className="text-sm text-primary mb-2"
                  >
                    ← Back to methods
                  </button>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">FaucetPay</p>
                      <p className="text-sm text-white/60">Instant crypto payment</p>
                    </div>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <p className="text-green-400 font-medium mb-2">How it works</p>
                    <ul className="text-sm text-white/70 space-y-1 list-disc list-inside">
                      <li>Pay using your FaucetPay balance</li>
                      <li>Supports BTC, ETH, LTC, DOGE & more</li>
                      <li>Instant credit after payment</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-2">Amount (points)</label>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Min 1,000"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    {(() => {
                      const fpMethod = availableMethods.find(m => m.id === 'faucetpay');
                      const pointsPerUsd = fpMethod?.pointsPerUsd || 1000;
                      const usdAmount = depositAmount ? (parseFloat(depositAmount) / pointsPerUsd).toFixed(4) : '0';
                      return <p className="text-xs text-white/50 mt-1">≈ ${usdAmount} USD</p>;
                    })()}
                  </div>

                  {error && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setDepositMethod(null);
                        setError(null);
                        setDepositAmount('');
                      }}
                      className="flex-1 py-4 rounded-xl bg-white/10 text-white font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeposit}
                      disabled={loading || !depositAmount}
                      className="flex-1 py-4 rounded-xl bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Pay with FaucetPay'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Crypto Address Display */}
              {depositAddress && (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <p className="text-green-400 font-medium mb-2">Deposit Address Generated</p>
                    <p className="text-sm text-white/70 mb-3">
                      Send exactly {depositAmount} points worth of {selectedNetwork.replace('_', ' ')} to:
                    </p>
                    <div className="bg-black/30 rounded-lg p-3 flex items-center gap-2">
                      <code className="text-sm text-white flex-1 break-all">{depositAddress}</code>
                      <button
                        onClick={() => copyAddress(depositAddress)}
                        className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
                      >
                        <Copy className="w-4 h-4 text-white/70" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium">Important</p>
                        <ul className="text-sm text-yellow-300/70 list-disc list-inside mt-1 space-y-1">
                          <li>Only send {selectedNetwork.replace('_', ' ')} via the correct network</li>
                          <li>Minimum 1 confirmation required</li>
                          <li>Balance updates within 5-10 minutes</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowDeposit(false);
                      setDepositAddress(null);
                      setDepositAmount('');
                      setDepositMethod(null);
                    }}
                    className="w-full py-4 rounded-xl bg-white/10 text-white font-medium"
                  >
                    Done
                  </button>
                </div>
              )}

              {/* FaucetPay Opened Confirmation */}
              {faucetPayData?.opened && (
                <div className="space-y-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                    <CheckCircle2 className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                    <p className="text-blue-400 font-medium mb-2">FaucetPay Opened!</p>
                    <p className="text-sm text-white/70">
                      Complete your payment in the browser window that just opened.
                    </p>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium">After Payment</p>
                        <ul className="text-sm text-yellow-300/70 list-disc list-inside mt-1 space-y-1">
                          <li>Your balance will update automatically</li>
                          <li>This usually takes a few seconds</li>
                          <li>You can close this dialog and continue</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowDeposit(false);
                      setFaucetPayData(null);
                      setDepositAmount('');
                      setDepositMethod(null);
                      // Refresh transactions to show the new deposit
                      fetchTransactions();
                    }}
                    className="w-full py-4 rounded-xl bg-blue-500 text-white font-semibold"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
